"use client";

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { X } from "lucide-react";
import YourBarrioLogo from "@/components/brand/YourBarrioLogo";

function isStandaloneDisplay() {
  if (typeof window === "undefined") return false;

  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true
  );
}

const DEFAULT_INSTALL_ENVIRONMENT = {
  isAndroid: false,
  isDesktop: false,
  isIos: false,
  isSafari: false,
};

let cachedEnvironmentKey = "";
let cachedInstallEnvironment = DEFAULT_INSTALL_ENVIRONMENT;

function getInstallEnvironment() {
  if (typeof window === "undefined") return DEFAULT_INSTALL_ENVIRONMENT;

  const userAgent = window.navigator?.userAgent || "";
  const platform = window.navigator?.platform || "";
  const maxTouchPoints = window.navigator?.maxTouchPoints || 0;
  const environmentKey = `${userAgent}|${platform}|${maxTouchPoints}`;
  if (environmentKey === cachedEnvironmentKey) {
    return cachedInstallEnvironment;
  }

  const isIos =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === "MacIntel" && maxTouchPoints > 1);
  const isSafari =
    /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|FxiOS|Edg|EdgiOS|OPR|OPiOS/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const isMobile = isIos || isAndroid || /Mobile/i.test(userAgent);

  cachedEnvironmentKey = environmentKey;
  cachedInstallEnvironment = {
    isAndroid,
    isDesktop: !isMobile,
    isIos,
    isSafari,
  };
  return cachedInstallEnvironment;
}

function subscribeInstallEnvironment() {
  return () => {};
}

export default function PwaInstallButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installOutcome, setInstallOutcome] = useState(null);
  const installEnvironment = useSyncExternalStore(
    subscribeInstallEnvironment,
    getInstallEnvironment,
    () => DEFAULT_INSTALL_ENVIRONMENT
  );
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    const returnFocus = () => returnFocusRef.current?.focus?.();
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(returnFocus);
    } else {
      returnFocus();
    }
  }, []);

  useEffect(() => {
    const media = window.matchMedia?.("(display-mode: standalone)");
    const handleDisplayModeChange = () => setIsInstalled(isStandaloneDisplay());
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
      setInstallOutcome(null);
    };
    const handleInstalled = () => {
      setInstallPrompt(null);
      setInstallOutcome("accepted");
      setIsInstalled(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    handleDisplayModeChange();
    if (media?.addEventListener) {
      media.addEventListener("change", handleDisplayModeChange);
    } else {
      media?.addListener?.(handleDisplayModeChange);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      if (media?.removeEventListener) {
        media.removeEventListener("change", handleDisplayModeChange);
      } else {
        media?.removeListener?.(handleDisplayModeChange);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeModal();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = dialogRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const firstControl = dialogRef.current?.querySelector(
      'button:not([disabled]), a[href], textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    firstControl?.focus?.();
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeModal, isOpen]);

  const modalContent = useMemo(() => {
    if (isInstalled) {
      return {
        body: "YourBarrio is already installed.",
        title: "YourBarrio is installed",
      };
    }

    if (installPrompt) {
      return {
        body: "Use your browser's install prompt to add YourBarrio to this device.",
        title: "Install YourBarrio",
      };
    }

    if (installEnvironment?.isIos && installEnvironment?.isSafari) {
      return {
        body: "Tap the Share button in Safari, then choose Add to Home Screen.",
        title: "Add YourBarrio to your Home Screen",
      };
    }

    if (installEnvironment?.isAndroid) {
      return {
        body: "Tap your browser menu, then choose Install app or Add to Home screen.",
        title: "Install YourBarrio",
      };
    }

    if (!installEnvironment?.isDesktop) {
      return {
        body: "Use your browser menu to add YourBarrio to your home screen.",
        title: "Add YourBarrio to your Home Screen",
      };
    }

    return {
      body: "Open yourbarrio.com on your phone, then add it from your browser menu.",
      title: "Add YourBarrio on mobile",
    };
  }, [installEnvironment, installPrompt, isInstalled]);

  const footerCopy = useMemo(() => {
    if (installPrompt) {
      return {
        ariaLabel: "Install YourBarrio. Browser install",
        primary: "Install YourBarrio",
        secondary: "Browser install",
      };
    }

    return {
      ariaLabel: "Add YourBarrio. Mobile home screen",
      primary: "Add YourBarrio",
      secondary: "Mobile home screen",
    };
  }, [installPrompt]);

  const handleInstall = async () => {
    if (!installPrompt) return;

    const promptEvent = installPrompt;
    setInstallPrompt(null);

    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      const outcome = choice?.outcome === "accepted" ? "accepted" : "dismissed";
      setInstallOutcome(outcome);
      if (outcome === "accepted") {
        setIsInstalled(true);
      }
    } catch {
      setInstallOutcome("dismissed");
    }
  };

  if (isInstalled) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          returnFocusRef.current = event.currentTarget;
          setIsOpen(true);
        }}
        className="group mt-6 inline-flex max-w-full items-center gap-2.5 rounded-xl border border-white/[0.075] bg-white/[0.028] px-3 py-2 text-left text-slate-200 transition duration-200 ease-out hover:border-[#a78bfa]/20 hover:bg-white/[0.052] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c4b5fd]/70 focus-visible:ring-offset-0 focus-visible:ring-offset-[#171920]"
        aria-label={footerCopy.ariaLabel}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/[0.07] bg-[#11131a] transition-colors duration-200 ease-out group-hover:border-[#a78bfa]/18">
          <YourBarrioLogo
            variant="mark-purple"
            alt=""
            aria-hidden="true"
            size={21}
            className="h-5 w-5 opacity-90"
          />
        </span>
        <span className="min-w-0">
          <span className="block text-[0.88rem] font-semibold leading-tight tracking-normal text-slate-200">
            {footerCopy.primary}
          </span>
          <span className="mt-0.5 block text-[0.7rem] font-medium leading-tight text-slate-400">
            {footerCopy.secondary}
          </span>
        </span>
      </button>

      {isOpen ? (
        <div
          className="fixed inset-0 z-[1000] flex items-end justify-center bg-black/62 px-4 py-5 sm:items-center"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <section
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pwa-install-title"
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#171920] p-5 text-white shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white">
                  <YourBarrioLogo
                    variant="mark-purple"
                    alt=""
                    aria-hidden="true"
                    size={26}
                    className="h-6 w-6"
                  />
                </span>
                <h2 id="pwa-install-title" className="text-base font-semibold tracking-normal">
                  {modalContent.title}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c4b5fd]"
                aria-label="Close install instructions"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <p className="mt-4 text-sm leading-6 text-slate-300">{modalContent.body}</p>

            {installOutcome === "dismissed" ? (
              <p className="mt-3 text-xs leading-5 text-slate-400">
                You can install YourBarrio later from this footer button.
              </p>
            ) : null}

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex justify-center rounded-xl border border-white/12 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c4b5fd]"
              >
                Got it
              </button>
              {installPrompt ? (
                <button
                  type="button"
                  onClick={handleInstall}
                  className="inline-flex justify-center rounded-xl bg-[#8b5cf6] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#7c3aed] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c4b5fd]"
                >
                  Install YourBarrio
                </button>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
